import type { LensCard, LensReadingCache } from '~/types/leLens';

import { type LensUrlState, encodeLensView } from '../lensUrlState';

/**
 * A stable fingerprint of everything a written reading depended on.
 *
 * Saved prose is the one part of a Lens that can quietly become wrong. A chart
 * redraws when a filter moves; a paragraph does not, so an insight saved in
 * August still sits under September's numbers claiming to describe them. This
 * is the fix: text is shown only while the signature it was written under
 * still matches the board in front of the reader.
 *
 * Two things go in. The encoded view carries filters, cross-filter selections,
 * measure ranges and the comparison, and is reused rather than reinvented so
 * the signature and the shareable link can never disagree about what the
 * reader is looking at. The card fingerprint covers the cards themselves:
 * which ones the section holds, and how each of them reads its data.
 *
 * The Lens version is deliberately NOT part of this. It looks like the obvious
 * way to catch a card edit, but saving a reading is itself a write, so a
 * version-keyed signature invalidates the very reading it was just stamped
 * onto. The card fingerprint catches an edit that changes the numbers without
 * being tripped by a save that does not.
 */
export function viewSignature(input: { view: LensUrlState; cards: ReadonlyArray<LensCard> }): string {
  const scope = encodeLensView(input.view) ?? 'default';
  return `v1|${scope}|${cardFingerprint(input.cards)}`;
}

/** The cards a section holds, in the order the spec lists them. */
export function sectionCards(cards: ReadonlyArray<LensCard>, sectionId: string): Array<LensCard> {
  return cards.filter((card) => card.section === sectionId);
}

/**
 * What each card is, and how it reads its data.
 *
 * Only the parts that change the numbers or the units they are read in. A
 * retitled card, a moved card or a resized card is the same answer, and
 * re-reading the section for one would spend a model call to produce the same
 * paragraph.
 */
function cardFingerprint(cards: ReadonlyArray<LensCard>): string {
  return cards
    .map((card) =>
      [
        card.id,
        card.type,
        card.datasetId ?? '',
        JSON.stringify(card.view ?? {}),
        JSON.stringify(card.config?.format ?? {}),
      ].join(':'),
    )
    .sort()
    .join(',');
}

/**
 * Whether saved text still describes what is on screen.
 *
 * Absent text and stale text are different outcomes and the caller must keep
 * them apart: absent means nothing has been written yet, stale means something
 * was and must not be shown.
 */
export function readingIsCurrent(cached: LensReadingCache | undefined, signature: string): boolean {
  return Boolean(cached?.content && cached.viewSignature === signature);
}
